<template>
  <div class="dashboard-layout">
    <!-- Mobile overlay -->
    <Transition name="overlay">
      <div
        v-if="mobileSidebarOpen"
        class="dashboard-layout__overlay"
        aria-label="Tutup menu navigasi"
        @click="closeMobileSidebar"
      />
    </Transition>

    <!-- Sidebar -->
    <Sidebar
      :menu="menuWithBadge"
      :collapsed="sidebarCollapsed"
      :collapsible="true"
      :mobile-open="mobileSidebarOpen"
      @toggle-collapse="toggleCollapse"
      @close-mobile="closeMobileSidebar"
    />

    <!-- Main area -->
    <div class="dashboard-layout__main">
      <DashboardHeader
        :section-title="sectionTitle"
        :page-title="pageTitle"
        :display-name="user.name"
        :role-label="user.roleLabel"
        :avatar="user.avatar"
        :notifications="notifications"
        @toggle-sidebar="handleToggleSidebar"
        @mark-all-read="markAllAsRead"
        @view-all-notifications="goToNotifications"
        @open-profile="handleOpenProfile"
        @logout="handleLogout"
      />

      <main class="dashboard-layout__content">
        <RouterView />
      </main>
    </div>
  </div>
</template>

<script>
import Sidebar from '@/components/common/Sidebar.vue'
import DashboardHeader from '@/components/common/DashboardHeader.vue'
import adminMenu from '@/config/AdminMenu.js'
import api, { resolveAvatarUrl } from '@/services/axios'
import authService from '@/services/authService'
import { useNotifications } from '@/composables/useNotifications'

export default {
  name: 'AdminLayouts',

  components: {
    Sidebar,
    DashboardHeader
  },

  setup() {
    const {
      notifications,
      markAllAsRead
    } = useNotifications({
      role: 'admin',
      endpoint: '/admin/notifications',
      pollInterval: 60000
    })

    return {
      notifications,
      markAllAsRead
    }
  },

  data() {
    return {
      adminMenu,
      sidebarCollapsed: false,
      mobileSidebarOpen: false,
      unreadSupportCount: 0,
      isLoadingUser: false,

      user: {
        name: 'Admin DITAP',
        roleLabel: 'Administrator',
        avatar: ''
      }
    }
  },

  computed: {
    sectionTitle() {
      return 'Admin'
    },

    pageTitle() {
      const routeName = this.$route.name

      if (!routeName) return 'Dashboard'

      return String(routeName)
        .replace(/^admin-/, '')
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    },

    menuWithBadge() {
      return this.adminMenu.map(group => ({
        ...group,
        items: group.items.map(item => {
          if (!item.badgeKey) return item

          let badge = 0

          if (item.badgeKey === 'support') {
            badge = this.unreadSupportCount
          }

          return {
            ...item,
            badge
          }
        })
      }))
    }
  },

  watch: {
    $route() {
      this.closeMobileSidebar()
      this.fetchUnreadSupport()
    }
  },

  mounted() {
    // Tampilkan data yang tersimpan terlebih dahulu.
    this.loadCachedUser()

    // Ambil data profil terbaru dari backend.
    this.loadUser()

    this.fetchUnreadSupport()
    this.handleResize()

    window.addEventListener('resize', this.handleResize)
    window.addEventListener('user-updated', this.handleUserUpdated)
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize)
    window.removeEventListener('user-updated', this.handleUserUpdated)

    document.body.style.overflow = ''
  },

  methods: {
    /**
     * Membaca profil sementara dari sessionStorage.
     * Ini membuat nama dan foto yang sudah tersimpan
     * dapat ditampilkan sebelum permintaan API selesai.
     */
    loadCachedUser() {
      try {
        const raw = sessionStorage.getItem('user')

        if (!raw) return

        const cachedUser = JSON.parse(raw)

        if (!cachedUser || typeof cachedUser !== 'object') return

        this.user = {
          name: this.getUserName(cachedUser),
          roleLabel: this.getRoleLabel(cachedUser),
          avatar: this.getAvatar(cachedUser)
        }
      } catch (error) {
        console.warn(
          'Gagal membaca data user dari sessionStorage:',
          error
        )
      }
    },

    /**
     * Mengambil profil terbaru dari backend.
     * Menggunakan endpoint yang sama dengan halaman profil.
     */
    async loadUser() {
      if (this.isLoadingUser) return

      this.isLoadingUser = true

      try {
        const raw = sessionStorage.getItem('user')
        let cachedUser = {}

        try {
          cachedUser = raw ? JSON.parse(raw) : {}
        } catch {
          cachedUser = {}
        }

        const response = await api.get('/profile')

        // Mendukung beberapa bentuk respons API.
        const responseData = response.data || {}
        const profile =
          responseData.data?.user ||
          responseData.user ||
          responseData.data ||
          responseData

        if (!profile || typeof profile !== 'object') {
          return
        }

        /*
         * Pertahankan data cache apabila field tertentu
         * tidak dikirim oleh respons API.
         */
        const updatedUser = {
          ...cachedUser,
          ...profile
        }

        const avatar =
          this.getAvatar(profile) ||
          this.getAvatar(cachedUser)

        updatedUser.avatar = profile.avatar || cachedUser.avatar || null
        updatedUser.avatar_url =
          profile.avatar_url ||
          cachedUser.avatar_url ||
          avatar ||
          null

        sessionStorage.setItem(
          'user',
          JSON.stringify(updatedUser)
        )

        this.user = {
          name: this.getUserName(updatedUser),
          roleLabel: this.getRoleLabel(updatedUser),
          avatar: avatar ? resolveAvatarUrl(avatar) : ''
        }
      } catch (error) {
        /*
         * Jika API gagal, nama dan foto dari cache tetap
         * digunakan. Tidak mengosongkan avatar yang ada.
         */
        console.error(
          'Gagal mengambil profil admin:',
          error.response?.data || error.message
        )
      } finally {
        this.isLoadingUser = false
      }
    },

    /**
     * Menentukan nama dari beberapa kemungkinan field.
     */
    getUserName(user) {
      return (
        user.name ||
        user.full_name ||
        user.username ||
        user.email ||
        'Admin DITAP'
      )
    },

    /**
     * Menentukan label role.
     */
    getRoleLabel(user) {
      const rawRole =
        user.role_name ||
        user.role?.name ||
        (typeof user.role === 'string' ? user.role : '') ||
        user.roleLabel ||
        'admin'

      const role = String(rawRole)

      return role.charAt(0).toUpperCase() + role.slice(1)
    },

    /**
     * Mendukung berbagai nama field foto profil.
     */
    getAvatar(user) {
      const avatar =
        user.avatar_url ||
        user.photo_url ||
        user.profile_photo_url ||
        user.photo ||
        user.avatar ||
        ''

      return avatar ? resolveAvatarUrl(avatar) : ''
    },

    /**
     * Dipanggil ketika halaman profil menyimpan
     * atau memperbarui data user.
     */
    handleUserUpdated() {
      this.loadCachedUser()
      this.loadUser()
    },

    async fetchUnreadSupport() {
      try {
        const response = await api.get('support/messages', {
          params: {
            status: 'new',
            per_page: 1
          }
        })

        this.unreadSupportCount = Number(
          response.data?.meta?.total || 0
        )
      } catch (error) {
        this.unreadSupportCount = 0
      }
    },

    handleResize() {
      const isMobile = window.innerWidth <= 768

      if (!isMobile) {
        this.mobileSidebarOpen = false
        document.body.style.overflow = ''
      }
    },

    handleToggleSidebar() {
      const isMobile = window.innerWidth <= 768

      if (isMobile) {
        this.mobileSidebarOpen = !this.mobileSidebarOpen

        document.body.style.overflow = this.mobileSidebarOpen
          ? 'hidden'
          : ''
      } else {
        this.toggleCollapse()
      }
    },

    toggleCollapse() {
      this.sidebarCollapsed = !this.sidebarCollapsed
    },

    closeMobileSidebar() {
      this.mobileSidebarOpen = false
      document.body.style.overflow = ''
    },

    goToNotifications() {
      this.$router.push({
        name: 'admin-notifications'
      })
    },

    handleOpenProfile() {
      this.$router.push({
        name: 'admin-profile'
      })
    },

    async handleLogout() {
      try {
        await authService.logout()
      } catch (error) {
        console.warn('Logout API gagal:', error)
      } finally {
        sessionStorage.removeItem('token')
        sessionStorage.removeItem('user')

        this.$router.push('/login')
      }
    }
  }
}
</script>

<style scoped lang="scss">
.dashboard-layout {
  display: flex;
  min-height: 100vh;
  background: var(--color-background, var(--color-bg, #ffffff));
  color: var(--color-text, #000a2d);
}

.dashboard-layout__main {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
  min-height: 100vh;
  background: var(--color-background, var(--color-bg, #ffffff));
}

.dashboard-layout__content {
  flex: 1;
  min-width: 0;
  padding: 24px;
  background: var(--color-background, var(--color-bg, #ffffff));
  color: var(--color-text, #000a2d);
}

.dashboard-layout__overlay {
  position: fixed;
  inset: 0;
  z-index: 90;
  background: rgb(0 0 0 / 50%);
  -webkit-tap-highlight-color: transparent;
}

.overlay-enter-active,
.overlay-leave-active {
  transition: opacity 0.2s ease;
}

.overlay-enter-from,
.overlay-leave-to {
  opacity: 0;
}

@media (max-width: 768px) {
  .dashboard-layout__content {
    padding: 16px;
  }
}

@media (max-width: 480px) {
  .dashboard-layout__content {
    padding: 12px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .overlay-enter-active,
  .overlay-leave-active {
    transition: none;
  }
}
</style>